import { useEffect } from 'react';

/**
 * useGlobalReveal
 * Globally discovers every content element (headings, paragraphs, cards, images,
 * button groups, badges, list items) across all pages and sections, applying a smooth
 * top-to-bottom scroll reveal animation with an intentional staggered delay.
 */
export default function useGlobalReveal(ready = true) {
  useEffect(() => {
    if (!ready) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const observedSet = new WeakSet();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('gr-visible', 'sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.04,
        rootMargin: '0px 0px -20px 0px',
      }
    );

    const scanAndObserve = () => {
      // Find all content candidates
      const rawCandidates = document.querySelectorAll(`
        h1, h2, h3, h4, h5, h6,
        p,
        .card, .sipl-card-hover,
        img,
        span.text-uppercase,
        .d-flex.gap-3,
        a.btn, button.btn,
        .badge,
        ul.list-unstyled > li,
        [class*="sipl-reveal"]
      `);

      rawCandidates.forEach((el) => {
        // Skip elements inside navigation, marquee, modals, or tab selectors
        if (el.closest('nav, .navbar, .sipl-marquee-track, .modal, .sipl-tabs-container, [role="tablist"]')) {
          return;
        }

        // Keep cards as a single cohesive unit (skip text/images inside cards)
        if (el.closest('.card, .sipl-card-hover') && !el.matches('.card, .sipl-card-hover')) {
          return;
        }

        // Avoid double-animating inline spans/links inside headings or paragraphs
        if (el.closest('h1, h2, h3, h4, h5, h6, p') !== el && el.matches('span, strong, em, b, i, a')) {
          return;
        }

        // Avoid double-animating buttons inside button containers
        if (el.matches('.btn') && el.closest('.d-flex.gap-3') && !el.matches('.d-flex.gap-3')) {
          return;
        }

        // Tag with data-gr if not already tagged
        if (!el.hasAttribute('data-gr')) {
          el.setAttribute('data-gr', '');
        }

        // Compute stagger delay per sibling group if not already specified in inline style
        if (!el.style.getPropertyValue('--gr-delay')) {
          const parent = el.parentElement;
          if (parent) {
            const siblings = Array.from(parent.querySelectorAll(':scope > [data-gr], :scope > [class*="sipl-reveal"]'));
            const sibIdx = Math.max(0, siblings.indexOf(el));
            // Slow, graceful stagger capped at 1s (1000ms) max
            const delay = Math.min(100 + sibIdx * 140, 1000);
            el.style.setProperty('--gr-delay', `${delay}ms`);
          }
        } else {
          // If delay exists, ensure it is capped at 1s maximum
          const currentDelay = el.style.getPropertyValue('--gr-delay');
          const num = parseFloat(currentDelay);
          if (currentDelay.includes('s') && !currentDelay.includes('ms')) {
            if (num > 1) el.style.setProperty('--gr-delay', '1s');
          } else if (num > 1000) {
            el.style.setProperty('--gr-delay', '1000ms');
          }
        }

        if (!observedSet.has(el)) {
          observedSet.add(el);
          observer.observe(el);
        }
      });
    };

    // Initial pass delayed slightly to let browser register initial paint
    const initialTimer = setTimeout(scanAndObserve, 60);

    // Rescan on route navigation or resize only (no heavy scroll polling)
    const handleCheck = () => {
      requestAnimationFrame(scanAndObserve);
    };

    window.addEventListener('resize', handleCheck, { passive: true });
    window.addEventListener('popstate', handleCheck, { passive: true });

    // MutationObserver to effortlessly catch client-side navigated pages without polling
    const mutationObserver = new MutationObserver(() => {
      scanAndObserve();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('resize', handleCheck);
      window.removeEventListener('popstate', handleCheck);
      mutationObserver.disconnect();
      observer.disconnect();
    };
  }, [ready]);
}
